/**
 * pages/PiutangUtang.js
 * Piutang & Utang — every currently-outstanding (non-Lunas) transaction, across three TABS
 * (Piutang & Utang batch 3 — replaces the old two-always-visible-cards layout with a true
 * single-view `.seg` toggle, default "Piutang"):
 *   - **Piutang**: real customers who owe. NO transfers.
 *   - **Utang**: real suppliers owed. NO transfers.
 *   - **Internal**: ONE ROW PER TRANSFER, never split by direction — from the whole business's
 *     own chair a transfer is one movement, not a debt in two directions. Each row's own Pihak
 *     cell states seller/buyer/who-owes-whom explicitly in plain Indonesian (see `renderRow`'s
 *     `internalPihakCell`) — the direction itself never flips, it's read straight off the
 *     transaction's own `transferDirection`/`branchId`.
 * `splitOutstanding` (`utils/piutangUtang.js`) does the three-way bucketing. A transaction drops
 * off this page entirely the moment it becomes Lunas, on every tab — this is a live filtered
 * view, not a history log. No creation flow here; Aksi reuses the exact same edit/print/Bayar
 * Tagihan/delete actions the main Penjualan/Pembelian tables already offer, wired identically
 * (same components, same write paths — see App.js).
 *
 * **The overdue banner is page-wide, not tab-scoped** — it counts across all three buckets
 * (deduped by `id`, since a future change could in principle put one transaction in more than
 * one bucket; today it never does, but `[...a, ...b, ...c].length` alone would silently stop
 * being correct the instant it did) regardless of which tab is currently active, same as the
 * old two-card layout already counted across both cards regardless of which were collapsed.
 */
import React, { useCallback, useMemo, useState } from "react";
import { fmtQty, fmtDateShort, today } from "../utils/idGenerators";
import { txnSisa, txnStatus, dueState } from "../utils/txnStatus";
import { bucketLabel } from "../utils/transformations";
import { useAvgCosts } from "../utils/wac";
import { useExpenses, LinkedExpensesDeleteNote } from "../components/ExpensesContext";
import { resolveBranchName, resolveLocationLabel } from "../utils/branches";
import { splitOutstanding, transferKlienName, transferDirectionLabel } from "../utils/piutangUtang";
import TransactionFormModal from "../components/TransactionFormModal";
import TransactionDetailModal from "../components/TransactionDetailModal";
import BayarTagihanModal from "../components/BayarTagihanModal";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import InvoicePusat from "../components/print/InvoicePusat";
import SuratJalanDialog from "../components/print/SuratJalanDialog";
import SuratJalanPusat from "../components/print/SuratJalanPusat";
import PerLineCollyDialog from "../components/print/PerLineCollyDialog";
import Checklist from "../components/print/Checklist";
import InvoiceCabang from "../components/print/InvoiceCabang";
import SuratJalanCabang from "../components/print/SuratJalanCabang";
import TxnRowActions from "../components/TxnRowActions";
import TxnNilaiCell from "../components/TxnNilaiCell";
import { SubtotalBarangCell, RincianTambahanCell, TotalSetelahBiayaCell } from "../components/TxnCostCells";
import TxnBarangStokCell from "../components/TxnBarangStokCell";
import { StatusPill, PaidStatusPill, DueBadge } from "../components/TxnBadges";

const TYPE_LABEL = { PENJUALAN: "Penjualan", PEMBELIAN: "Pembelian", TRANSFER: "Transfer" };

const TABS = [
  { value: "piutang", label: "Piutang" },
  { value: "hutang", label: "Utang" },
  { value: "internal", label: "Internal" },
];

// Own local declaration, not a shared import — mirrors Penjualan.js's/Pembelian.js's own
// independently-declared STOK_COL_WIDTH (see TxnBarangStokCell.js's header comment for why
// this stays a single source of truth per page rather than a shared constant).
const STOK_COL_WIDTH = 110;

const SORT_OPTIONS = [
  { value: "due_asc", label: "Jatuh Tempo Terdekat" },
  { value: "due_desc", label: "Jatuh Tempo Terjauh" },
  { value: "sisa_asc", label: "Outstanding Terkecil" },
  { value: "sisa_desc", label: "Outstanding Terbesar" },
];

// Same "isPembelian ? supplierInvoiceNo : invoiceNumber" convention `renderRow` uses for its
// own No. Invoice cell — shared here so search can never disagree with what's actually shown.
function invoiceNoOf(t) {
  return t.type === "PEMBELIAN" ? (t.supplierInvoiceNo || "") : (t.invoiceNumber || "");
}

// No. Invoice searchable alongside Klien — both match against the SAME search box input, one
// combined OR (not a scoped Semua/Klien/No. Invoice selector like Penjualan.js's own search —
// this page's confirmed design is a single always-both-fields box).
function filterBySearch(list, needle, nameOf) {
  if (!needle) return list;
  return list.filter((t) =>
    nameOf(t).toLowerCase().includes(needle) || invoiceNoOf(t).toLowerCase().includes(needle)
  );
}

// Internal tab's own search predicate (batch 3) — matches EITHER party's name, since a reader
// searching "AMIN" should find a transfer whether AMIN is the sender or the receiver. Piutang/
// Utang reuse the existing klienNameOf instead (one real counterparty per row there).
function internalPartyNameOf(t, branches) {
  return `${resolveLocationLabel(t.location, t.branchId, branches)} ${transferKlienName(t, branches)}`;
}

function sortTxns(list, sortBy) {
  const sorted = [...list];
  sorted.sort((a, b) => {
    if (sortBy === "due_asc" || sortBy === "due_desc") {
      const da = a.dueDate;
      const db = b.dueDate;
      if (!da && !db) return 0;
      if (!da) return 1; // no due date sorts last regardless of direction
      if (!db) return -1;
      if (da === db) return 0;
      const cmp = da < db ? -1 : 1;
      return sortBy === "due_asc" ? cmp : -cmp;
    }
    const sa = txnSisa(a);
    const sb = txnSisa(b);
    return sortBy === "sisa_asc" ? sa - sb : sb - sa;
  });
  return sorted;
}

export default function PiutangUtang({
  transactions = [], items = [], contacts = [], salespeople = [], persons = [], branches = [], siblingLinks = [], company, defaultDueDays,
  onUpdateTransaction, onDeleteTransaction, onSavePacking, onSaveAddressOverride, onRecordPayment,
  onCreateContact, onCreateContactFull, onCreateSalespersonRole, onOpenItem,
}) {
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("due_asc");
  const [tab, setTab] = useState("piutang"); // "piutang" | "hutang" | "internal"
  const [formModal, setFormModal] = useState(null); // null | { mode: "edit", type, initial }
  const [detailTxnId, setDetailTxnId] = useState(null);
  const [bayarTagihanTxnId, setBayarTagihanTxnId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [printTarget, setPrintTarget] = useState(null);
  const [suratJalanDialogTarget, setSuratJalanDialogTarget] = useState(null);
  const [suratJalanPrintTarget, setSuratJalanPrintTarget] = useState(null);
  const [checklistDialogTarget, setChecklistDialogTarget] = useState(null);
  const [checklistPrintTarget, setChecklistPrintTarget] = useState(null);
  const [invoiceCabangDialogTarget, setInvoiceCabangDialogTarget] = useState(null);
  const [invoiceCabangPrintTarget, setInvoiceCabangPrintTarget] = useState(null);
  const [suratJalanCabangDialogTarget, setSuratJalanCabangDialogTarget] = useState(null);
  const [suratJalanCabangPrintTarget, setSuratJalanCabangPrintTarget] = useState(null);

  const byCode = useMemo(() => new Map(items.map((it) => [it.internalCode, it])), [items]);
  const contactsById = useMemo(() => new Map(contacts.map((c) => [c.id, c])), [contacts]);

  const detailTxn = detailTxnId ? transactions.find((t) => t.id === detailTxnId) || null : null;
  const bayarTagihanTxn = bayarTagihanTxnId ? transactions.find((t) => t.id === bayarTagihanTxnId) || null : null;

  // A purchase's own costs (Tagihan/Potongan/linked Biaya Lain-Lain) go into HPP — utils/wac.js.
  const { expenses } = useExpenses();
  const avgCosts = useAvgCosts(transactions, expenses);

  const { piutang, hutang, internal } = useMemo(() => splitOutstanding(transactions), [transactions]);

  const klienNameOf = useCallback(
    (t) => (t.type === "TRANSFER" ? transferKlienName(t, branches) : (contactsById.get(t.contactId)?.name || "—")),
    [branches, contactsById]
  );
  const internalNameOf = useCallback((t) => internalPartyNameOf(t, branches), [branches]);

  const needle = q.trim().toLowerCase();
  const piutangVisible = useMemo(
    () => sortTxns(filterBySearch(piutang, needle, klienNameOf), sortBy),
    [piutang, needle, sortBy, klienNameOf]
  );
  const hutangVisible = useMemo(
    () => sortTxns(filterBySearch(hutang, needle, klienNameOf), sortBy),
    [hutang, needle, sortBy, klienNameOf]
  );
  const internalVisible = useMemo(
    () => sortTxns(filterBySearch(internal, needle, internalNameOf), sortBy),
    [internal, needle, sortBy, internalNameOf]
  );

  // Dedup by id (see this file's own header comment) — a transaction only ever lands in exactly
  // one of the three buckets today, but the overdue banner is page-wide and this must stay
  // correct even if a future change ever made that overlap possible.
  const overdueCount = useMemo(() => {
    const byId = new Map();
    for (const t of [...piutang, ...hutang, ...internal]) byId.set(t.id, t);
    return [...byId.values()].filter((t) => dueState(t, today()).kind === "overdue").length;
  }, [piutang, hutang, internal]);

  const openEdit = (t) => setFormModal({ mode: "edit", type: t.type, initial: t });
  const submitForm = (draft) => {
    onUpdateTransaction(formModal.initial.id, draft);
    setFormModal(null);
  };

  // `pihakCell`/`statusCell` overrides (batch 3): the Internal tab reuses every other column
  // byte-for-byte (same colgroup, same Barang/Stok/money/Jatuh Tempo/Aksi rendering — a
  // transfer row's `isTransfer`/print-gating logic already works correctly here since
  // `isPembelian` is always false for a TRANSFER) and only overrides these two cells — Pihak
  // (sell/buy/debt sentence instead of one name) and Status (Lunas/Belum Lunas instead of
  // Piutang/Utang, since a whole-business view must never label a transfer with a direction).
  const renderRow = (t, { pihakCell, statusCell } = {}) => {
    const isTransfer = t.type === "TRANSFER";
    const isPembelian = t.type === "PEMBELIAN";
    const due = dueState(t, today());
    const lines = t.lines || [];
    const invoiceNo = invoiceNoOf(t) || "—";
    const destBucket = t.location === "PUSAT" ? "CABANG" : "PUSAT"; // TRANSFER only

    return (
      <tr key={t.id} onClick={() => setDetailTxnId(t.id)} className={isTransfer ? "txn-row--transfer" : undefined}>
        <td className="date-cell">{fmtDateShort(t.date)}<br /><span style={{ color: "var(--muted)", fontSize: 12 }}>{t.time}</span></td>
        <td className="mono" style={{ color: isPembelian ? "#555" : "var(--blue)" }}>
          {invoiceNo}
          {(t.costItems || []).length > 0 && <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>+Rincian</span>}
        </td>
        <td className="klien-cell">{pihakCell ? pihakCell(t) : klienNameOf(t)}</td>
        <td className="jenis-cell">{TYPE_LABEL[t.type] || t.type}</td>
        <TxnBarangStokCell
          lines={lines}
          byCode={byCode}
          stokWidth={STOK_COL_WIDTH}
          renderStok={(l) =>
            isPembelian ? (
              <span style={{ color: "var(--green)" }}>+{fmtQty(l.qty)} PCS</span>
            ) : isTransfer ? (
              <>
                <span style={{ color: "var(--red)" }}>−{fmtQty(l.qty)} PCS {bucketLabel(t.location)}</span>
                {" → "}
                <span style={{ color: "var(--green)" }}>+{fmtQty(l.qty)} PCS {bucketLabel(destBucket)}</span>
              </>
            ) : (
              <span style={{ color: "var(--red)" }}>−{fmtQty(l.qty)} PCS</span>
            )
          }
        />
        <SubtotalBarangCell txn={t} />
        <RincianTambahanCell txn={t} />
        <TxnNilaiCell txn={t} />
        <TotalSetelahBiayaCell txn={t} />
        <td className="status-cell">{statusCell ? statusCell(t) : <StatusPill status={txnStatus(t)} />}</td>
        <td className="due-cell">{due.kind === "none" ? "—" : <DueBadge due={due} />}</td>
        <td className="row-actions aksi-cell" onClick={(e) => e.stopPropagation()}>
          <TxnRowActions
            onEdit={() => openEdit(t)}
            onBayarTagihan={() => setBayarTagihanTxnId(t.id)}
            txnType={t.type}
            onPrintInvoicePusat={!isPembelian && t.location === "PUSAT" ? () => setPrintTarget(t) : undefined}
            onPrintSuratJalanPusat={!isPembelian && t.location === "PUSAT" ? () => setSuratJalanDialogTarget(t) : undefined}
            onPrintChecklist={!isPembelian && t.location === "PUSAT" ? () => setChecklistDialogTarget(t) : undefined}
            onPrintInvoiceCabang={!isPembelian && t.location === "CABANG" ? () => setInvoiceCabangDialogTarget(t) : undefined}
            onPrintSuratJalanCabang={!isPembelian && t.location === "CABANG" ? () => setSuratJalanCabangDialogTarget(t) : undefined}
            onDelete={() => setDeleteTarget(t)}
          />
        </td>
      </tr>
    );
  };

  // Internal tab's own Pihak cell (batch 3) — two lines: "{pengirim} → {penerima}" (bold,
  // reusing the EXACT SAME vocabulary Pembelian.js's "Penjual"/Penjualan.js's "Pembeli" columns
  // already established for a transfer's two sides — the source IS the seller, the destination
  // IS the buyer), then a muted line stating the debt explicitly, so a row reads without the
  // reader working anything out. The bold line itself is `transferDirectionLabel`
  // (`utils/piutangUtang.js`, section F batch 5) — extracted so KontakPage's own Riwayat
  // Transaksi can show the IDENTICAL "{source} → {destination}" string on an internal contact's
  // transfer rows instead of inventing a second phrasing; `pengirim`/`penerima` stay local here
  // too, since the muted second line needs them SEPARATELY for its own sentence. Direction is
  // read straight off `transferDirection`/`branchId` via the same `resolveLocationLabel`/
  // `transferKlienName` helpers the rest of the app already uses for a transfer's two sides —
  // never flips depending on who's "looking."
  const internalPihakCell = (t) => {
    const pengirim = resolveLocationLabel(t.location, t.branchId, branches);
    const penerima = transferKlienName(t, branches);
    return (
      <>
        <div><b>{transferDirectionLabel(t, branches)}</b></div>
        <div style={{ color: "var(--muted)", fontSize: 12, marginTop: "var(--gap-xs)" }}>{penerima} berhutang kepada {pengirim}</div>
      </>
    );
  };
  // Lunas/Belum Lunas, never Piutang/Utang — see PaidStatusPill's own header comment. Every row
  // on this tab is already Belum Lunas by construction (splitOutstanding only ever puts an
  // outstanding Transfer in `internal`), so this will always render "Belum Lunas" today — kept
  // real rather than hardcoded so it stays correct if that filtering is ever relaxed.
  const internalStatusCell = (t) => <PaidStatusPill lunas={txnSisa(t) === 0} />;

  const renderSection = ({ title, full, visible, renderRowFn }) => {
    const totalOutstanding = full.reduce((s, t) => s + txnSisa(t), 0);
    return (
      <div className="card" style={{ marginBottom: 18, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", background: "#f8fafc" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--gap-md)" }}>
            <h2 style={{ margin: 0, fontSize: 16 }}>{title}</h2>
            <span className="chip chip--muted">{full.length} transaksi</span>
          </div>
          <div style={{ fontWeight: 700, color: "#92400e" }}>Rp {fmtQty(totalOutstanding)}</div>
        </div>
        {full.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--muted)" }}>Belum ada {title.toLowerCase()}.</div>
        ) : visible.length === 0 ? (
          <div className="empty-state">Tidak ada {title.toLowerCase()} yang cocok dengan pencarian.</div>
        ) : (
          <div className="table-wrap">
            <table className="table txn-table txn-table--fixed">
              <colgroup>
                <col style={{ width: 95 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 150 }} />
                {/* Jenis — real-measured (not guessed): this table's cells render at Segoe UI
                    14px/400 with 12px horizontal padding each side; the actual displayed text
                    is Title Case via TYPE_LABEL ("Penjualan"/"Pembelian"/"Transfer"), not the
                    all-caps enum names. Measured ink widths (GDI+ TextRenderer, NoPadding —
                    the same class of real-font-metric check TxnBarangStokCell.js's own Barang
                    calibration used): Penjualan 67px, Pembelian 71px (the real longest),
                    Transfer 57px. 71px + 24px padding = a 95px hard minimum; 105px adds a 10px
                    safety margin for cross-renderer variance. */}
                <col style={{ width: 105 }} />
                <col style={{ width: 215 }} />
                <col style={{ width: STOK_COL_WIDTH }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 150 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 140 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Tanggal &amp; Waktu</th>
                  <th>No. Invoice</th>
                  <th className="klien-cell">Pihak</th>
                  <th className="jenis-cell">Jenis</th>
                  <th>Barang</th>
                  <th className="num">Stok</th>
                  <th className="num">Subtotal Barang</th>
                  <th className="num">Tagihan &amp; Potongan</th>
                  <th className="num">Total Nilai</th>
                  <th className="num">Total Setelah Biaya</th>
                  <th className="status-cell">Status</th>
                  <th className="due-cell">Jatuh Tempo</th>
                  <th className="aksi-cell">Aksi</th>
                </tr>
              </thead>
              <tbody>{visible.map((t) => renderRowFn(t))}</tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Piutang &amp; Utang</h1>
          <div className="page-sub">Setiap transaksi yang belum Lunas — hilang dari daftar ini begitu Lunas.</div>
        </div>
      </div>

      {overdueCount > 0 && (
        <div style={{ background: "#fef3c7", color: "#92400e", borderRadius: 8, padding: "10px 16px", marginBottom: "var(--gap-lg)", fontWeight: 600 }}>
          ⚠ {overdueCount} transaksi telah jatuh tempo.
        </div>
      )}

      <div className="seg" style={{ marginBottom: "var(--gap-lg)" }}>
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            className={tab === t.value ? "active" : ""}
            onClick={() => setTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="toolbar">
        <input className="form-input grow" placeholder="Cari nama pihak atau no. invoice…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="form-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {tab === "piutang" && renderSection({ title: "Piutang", full: piutang, visible: piutangVisible, renderRowFn: renderRow })}
      {tab === "hutang" && renderSection({ title: "Utang", full: hutang, visible: hutangVisible, renderRowFn: renderRow })}
      {tab === "internal" && renderSection({
        title: "Internal", full: internal, visible: internalVisible,
        renderRowFn: (t) => renderRow(t, { pihakCell: internalPihakCell, statusCell: internalStatusCell }),
      })}

      {formModal && (
        <TransactionFormModal
          mode={formModal.mode}
          type={formModal.type}
          initial={formModal.initial}
          items={items}
          contacts={contacts}
          salespeople={salespeople}
          persons={persons}
          branches={branches}
          siblingLinks={siblingLinks}
          avgCosts={avgCosts}
          defaultDueDays={defaultDueDays}
          onCreateContact={onCreateContact}
          onCreateContactFull={onCreateContactFull}
          onCreateSalespersonRole={onCreateSalespersonRole}
          onSubmit={submitForm}
          onClose={() => setFormModal(null)}
        />
      )}

      {detailTxn && (
        <TransactionDetailModal
          txn={detailTxn}
          items={items}
          contacts={contacts}
          salespeople={salespeople}
          persons={persons}
          branches={branches}
          today={today()}
          onClose={() => setDetailTxnId(null)}
        />
      )}

      {bayarTagihanTxn && (
        <BayarTagihanModal
          txn={bayarTagihanTxn}
          contacts={contacts}
          branches={branches}
          onRecordPayment={onRecordPayment}
          onClose={() => setBayarTagihanTxnId(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          title="Hapus transaksi?"
          message={<>
            Anda akan menghapus transaksi <b className="mono">{deleteTarget.invoiceNumber || deleteTarget.supplierInvoiceNo || deleteTarget.id}</b>.
            Stok yang sudah bergerak akan dikembalikan.<LinkedExpensesDeleteNote txnId={deleteTarget.id} /> Tindakan ini permanen.
          </>}
          requireText="HAPUS"
          onConfirm={() => {
            onDeleteTransaction(deleteTarget.id);
            if (detailTxnId === deleteTarget.id) setDetailTxnId(null);
            setDeleteTarget(null);
          }}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {printTarget && (
        <InvoicePusat
          txn={printTarget}
          items={items}
          company={company}
          onSaveAddressOverride={onSaveAddressOverride}
          onClose={() => setPrintTarget(null)}
        />
      )}

      {suratJalanDialogTarget && (
        <SuratJalanDialog
          txn={suratJalanDialogTarget}
          onSubmit={(payload) => {
            onSavePacking(suratJalanDialogTarget.id, payload);
            setSuratJalanPrintTarget({ ...suratJalanDialogTarget, packing: payload.packing });
            setSuratJalanDialogTarget(null);
          }}
          onClose={() => setSuratJalanDialogTarget(null)}
        />
      )}
      {suratJalanPrintTarget && (
        <SuratJalanPusat
          txn={suratJalanPrintTarget}
          onSaveAddressOverride={onSaveAddressOverride}
          onClose={() => setSuratJalanPrintTarget(null)}
        />
      )}

      {checklistDialogTarget && (
        <PerLineCollyDialog
          txn={checklistDialogTarget}
          items={items}
          title="Data Checklist"
          onSubmit={(payload) => {
            onSavePacking(checklistDialogTarget.id, payload);
            setChecklistPrintTarget({ ...checklistDialogTarget, lines: payload.lines });
            setChecklistDialogTarget(null);
          }}
          onClose={() => setChecklistDialogTarget(null)}
        />
      )}
      {checklistPrintTarget && (
        <Checklist
          txn={checklistPrintTarget}
          items={items}
          contact={contactsById.get(checklistPrintTarget.contactId)}
          branches={branches}
          company={company}
          onClose={() => setChecklistPrintTarget(null)}
        />
      )}

      {invoiceCabangDialogTarget && (
        <PerLineCollyDialog
          txn={invoiceCabangDialogTarget}
          items={items}
          title="Data Invoice Cabang"
          onSubmit={(payload) => {
            onSavePacking(invoiceCabangDialogTarget.id, payload);
            setInvoiceCabangPrintTarget({ ...invoiceCabangDialogTarget, lines: payload.lines });
            setInvoiceCabangDialogTarget(null);
          }}
          onClose={() => setInvoiceCabangDialogTarget(null)}
        />
      )}
      {invoiceCabangPrintTarget && (
        <InvoiceCabang
          txn={invoiceCabangPrintTarget}
          items={items}
          cabangName={resolveBranchName(branches, invoiceCabangPrintTarget.branchId) || undefined}
          onSaveAddressOverride={onSaveAddressOverride}
          onClose={() => setInvoiceCabangPrintTarget(null)}
        />
      )}

      {suratJalanCabangDialogTarget && (
        <SuratJalanDialog
          txn={suratJalanCabangDialogTarget}
          onSubmit={(payload) => {
            onSavePacking(suratJalanCabangDialogTarget.id, payload);
            setSuratJalanCabangPrintTarget({ ...suratJalanCabangDialogTarget, packing: payload.packing });
            setSuratJalanCabangDialogTarget(null);
          }}
          onClose={() => setSuratJalanCabangDialogTarget(null)}
        />
      )}
      {suratJalanCabangPrintTarget && (
        <SuratJalanCabang
          txn={suratJalanCabangPrintTarget}
          onSaveAddressOverride={onSaveAddressOverride}
          onClose={() => setSuratJalanCabangPrintTarget(null)}
        />
      )}
    </div>
  );
}
